/**
 * Accua Cookie Consent JavaScript
 *
 * This script handles all client-side functionality for the Accua Cookie Consent plugin.
 * It manages cookie consent preferences, displays/hides consent dialogs, and controls
 * loading of blocked content based on user consent decisions.
 *
 * Plain JavaScript since 6.8.0. The plugin used to depend on jQuery, which made
 * WordPress print jQuery and jQuery Migrate (~95 KB) as render-blocking scripts on
 * every page of sites whose theme does not use it.
 *
 * Settings come from the JSON block PHP prints with the dialog
 * (#accua-cookie-config, read by js/boot.js first). Pages cached by an older
 * version carry the previous format instead (accua_cookie_data from
 * wp_localize_script and one <template> per language); both are understood, so
 * an update never breaks a page a cache is still serving.
 *
 * @since 1.0.0
 */
(function () {
    'use strict';

    var d = document;

    /* ------------------------------------------------------------------
     * Small DOM helpers (what jQuery used to provide)
     * ------------------------------------------------------------------ */

    function hasOwn(obj, key) {
        return Object.prototype.hasOwnProperty.call(obj, key);
    }

    function qsa(selector, root) {
        try {
            return Array.prototype.slice.call((root || d).querySelectorAll(selector));
        } catch (e) {
            return [];
        }
    }

    /** Iterate an array or a plain object; returning false stops (like $.each). */
    function each(obj, fn) {
        if (!obj || typeof obj !== 'object') {
            return;
        }
        if (Array.isArray(obj)) {
            for (var i = 0; i < obj.length; i++) {
                if (fn(i, obj[i]) === false) {
                    return;
                }
            }
            return;
        }
        for (var k in obj) {
            if (hasOwn(obj, k) && fn(k, obj[k]) === false) {
                return;
            }
        }
    }

    function extend(target, source) {
        each(source, function (k, v) {
            target[k] = v;
        });
        return target;
    }

    /**
     * Category slugs renamed by earlier versions, old => current (6.8.1).
     *
     * 5.0.0 merged "targeting" into "marketing" but left the position rules
     * alone, and no settings migration ran after 6.0.0, so a site can still have
     * rules naming "targeting". PHP rewrites them in every page it renders now
     * (accua_cookie_resolve_legacy_rule_categories()); this covers pages a
     * cache stored before the update. See resolveCategory().
     */
    var LEGACY_CATEGORIES = { targeting: 'marketing' };

    function hasClassName(el, names) {
        for (var i = 0; i < names.length; i++) {
            if (el.classList.contains(names[i])) {
                return true;
            }
        }
        return false;
    }

    var defaultDisplays = {};

    /** The display value an element of this type gets when shown (as $.fn.show). */
    function defaultDisplay(el) {
        var tag = el.nodeName;
        if (!defaultDisplays[tag]) {
            var probe = d.createElement(tag);
            d.body.appendChild(probe);
            var display = window.getComputedStyle(probe).display;
            probe.parentNode.removeChild(probe);
            defaultDisplays[tag] = (!display || display === 'none') ? 'block' : display;
        }
        return defaultDisplays[tag];
    }

    function isHidden(el) {
        return window.getComputedStyle(el).display === 'none';
    }

    function show(el) {
        if (el.style.display === 'none') {
            el.style.display = '';
        }
        if (el.isConnected !== false && isHidden(el)) {
            el.style.display = defaultDisplay(el);
        }
    }

    function hide(el) {
        el.style.display = 'none';
    }

    function remove(el) {
        if (el.parentNode) {
            el.parentNode.removeChild(el);
        }
    }

    var DURATIONS = { slow: 600, fast: 200 };

    /** Opacity fade used by the legacy banner and popovers ($.fn.fadeIn). */
    function fadeIn(el, speed) {
        if (!isHidden(el)) {
            return;
        }
        var ms = DURATIONS[speed] || 400;
        el.style.opacity = '0';
        show(el);
        el.style.transition = 'opacity ' + ms + 'ms';
        void el.offsetWidth; // start from opacity 0
        el.style.opacity = '1';
        setTimeout(function () {
            el.style.transition = '';
            el.style.opacity = '';
        }, ms);
    }

    function fadeOut(el, speed) {
        if (isHidden(el)) {
            return;
        }
        var ms = DURATIONS[speed] || 400;
        el.style.transition = 'opacity ' + ms + 'ms';
        el.style.opacity = '0';
        setTimeout(function () {
            hide(el);
            el.style.transition = '';
            el.style.opacity = '';
        }, ms);
    }

    /** Run code in the global scope ($.globalEval). */
    function globalEval(code) {
        var script = d.createElement('script');
        script.text = code;
        d.head.appendChild(script).parentNode.removeChild(script);
    }

    function on(selector, type, handler) {
        qsa(selector).forEach(function (el) {
            el.addEventListener(type, handler);
        });
    }

    /** Delegated listener on the document ($(document).on(type, selector, fn)). */
    function delegate(type, selector, handler) {
        d.addEventListener(type, function (e) {
            var target = e.target && e.target.closest ? e.target.closest(selector) : null;
            if (target) {
                handler.call(target, e);
            }
        });
    }

    function onReady(fn) {
        if (d.readyState === 'loading') {
            d.addEventListener('DOMContentLoaded', fn);
        } else {
            setTimeout(fn, 0);
        }
    }

    /**
     * Main Accua Cookie Consent handler
     *
     * @namespace accuaCookieConsent
     */
    const accuaCookieConsent = {
        // Store user preferences
        preferences: {},
        denied: [],
        accepted: [],
        // True when the saved cookie is missing categories that are now active
        // (re-prompt case): dismissing the dialog must rewrite the cookie
        newSettingsPending: false,
        // Crawler user agent with "skip bots" on (decided by js/boot.js)
        isBot: false,

        /**
         * Initialize the cookie consent functionality
         *
         * @function init
         * @memberof accuaCookieConsent
         */
        init: function () {
            // Settings: the page config (6.8.0+), or the localized data of a
            // page cached by an older version.
            this.boot = (typeof window.accuaCookieBootRun === 'function' ? window.accuaCookieBootRun() : null) ||
                window.accuaCookieBoot || null;
            if (this.boot && this.boot.config) {
                const c = this.boot.config;
                this.data = window.accua_cookie_data = extend({}, c.core);
                this.data.supportedLangs = c.langs;
                this.data.fallbackLang = c.fb;
                this.data.lang = c.il;
                window.accua_modal_data = extend({}, c.modal);
                this.isBot = !!this.boot.bot;
            } else if (typeof window.accua_cookie_data !== 'undefined') {
                this.data = window.accua_cookie_data;
            } else {
                return;
            }

            // Pages listed in "Disattiva sempre il popup in queste pagine"
            // (cookie/privacy policy and the like): the dialog must not open
            // by itself here. PHP decides it from the URL alone, so the flag
            // travels safely inside cached HTML. Nothing else changes — the
            // floating icon and every [accua-cookie-dialog] link still open
            // the dialog, and no consent is assumed: an undecided visitor
            // keeps the strictest settings, exactly as on any other page.
            this.popupDisabled = !!(this.data && this.data.popupDisabled === '1');

            // Resolve the UI language from the browser and swap the localized
            // strings. The page HTML is cached and served to every visitor,
            // so the server cannot pick the language per visitor.
            this.applyLanguage();

            // PHP ships the "content blocked" placeholders empty because the page
            // HTML is cached; fill them now that the language is resolved, before
            // anything can show one.
            this.fillBlockedPlaceholders();

            // Pages cached by an older version: clone the browser-language
            // modal template into the DOM before any UI work (initializeUI()
            // syncs checkbox state from the cookie, so the markup must exist).
            this.instantiateModalTemplate();

            this.currentCookieValue = this.getCookie();

            // Remove the one-time revocation-reload marker from the address bar
            this.stripReloadMarker();

            // Firefox restores each iframe's session history on plain reloads:
            // a revoked embed can come back visible (and hit the network) even
            // though its src attribute is "about:blank". Force blocked frames
            // back to about:blank before applying preferences.
            this.neutralizeRestoredFrames();

            // Setup event listeners
            this.setupEventListeners();

            // Initialize UI based on cookie state
            this.initializeUI();

            // From here on the classes below drive the floating icon; the
            // bootstrap's page-level class only covered the time before.
            d.documentElement.classList.remove('accua-cc-undecided');
        },

        /**
         * Resolve the consent UI language from the browser.
         *
         * Matches navigator.languages (in preference order) against the
         * site's supported languages; falls back to the server-computed
         * fallback (English when the site offers it, otherwise the main
         * site language).
         *
         * @function resolveLanguage
         * @memberof accuaCookieConsent
         * @returns {string} Two-letter language code
         */
        resolveLanguage: function () {
            if (this.boot && this.boot.lang) {
                return this.boot.lang;
            }

            const supported = (this.data && this.data.supportedLangs) || [];
            const fallback = (this.data && (this.data.fallbackLang || this.data.lang)) || 'en';

            if (!supported.length) {
                return fallback;
            }

            const navLangs = (navigator.languages && navigator.languages.length)
                ? navigator.languages
                : [navigator.language || ''];

            for (let i = 0; i < navLangs.length; i++) {
                const two = String(navLangs[i] || '').toLowerCase().substring(0, 2);
                if (supported.indexOf(two) !== -1) {
                    return two;
                }
            }

            return fallback;
        },

        /**
         * Apply the resolved language: overlay the per-language string maps
         * onto the data objects and refresh server-rendered bits (floating
         * icon label). The dialog's own texts were swapped by js/boot.js.
         *
         * @function applyLanguage
         * @memberof accuaCookieConsent
         */
        applyLanguage: function () {
            const lang = this.resolveLanguage();
            this.lang = lang;
            const modalData = window.accua_modal_data;

            if (this.boot && this.boot.config) {
                // The page's own language is in the config, any other one in
                // its language pack (loaded by js/boot.js before this ran)
                const c = this.boot.config;
                const packs = window.accuaCookiePacks || {};
                const inline = c.i18n || {};
                // (the legacy banner carries every language inline)
                const t = (lang !== c.il && packs[lang]) ? packs[lang] : (inline[lang] || inline[c.il]);
                if (t) {
                    const core = t.core || {};
                    this.data.buttonUpdateText = core.buttonUpdateText;
                    this.data.buttonAcceptText = core.buttonAcceptText;
                    this.data.blockedMessage = core.blockedMessage;
                    each(this.data.classesData, function (k, v) {
                        if (core.labels && hasOwn(core.labels, k)) {
                            v.label = core.labels[k];
                        }
                    });
                    if (modalData) {
                        extend(modalData, t.modal);
                    }
                }
                this.data.lang = lang;
                if (modalData) {
                    modalData.lang = lang;
                }
            } else {
                // Page cached by an older version: i18n maps in the localized data
                if (this.data && this.data.i18n && this.data.i18n[lang]) {
                    extend(this.data, this.data.i18n[lang]);
                    this.data.lang = lang;
                }
                if (modalData && modalData.i18n && modalData.i18n[lang]) {
                    extend(modalData, modalData.i18n[lang]);
                    modalData.lang = lang;
                }
            }

            if (modalData && modalData.floatingIconLabel) {
                qsa('.accua-cookie-floating-icon').forEach(function (icon) {
                    icon.setAttribute('aria-label', modalData.floatingIconLabel);
                });
            }
        },

        /**
         * Instantiate the modal markup for the resolved language.
         *
         * Only for pages cached by a version before 6.8.0, which output one
         * inert <template class="accua-modal-template"> per supported language;
         * the browser-matching copy is cloned into the DOM here and all
         * templates are removed. Since 6.8.0 the dialog is live in the page.
         * Must run before initializeUI() and before AccuaAccessibleModal.init()
         * (scripts-modal.js).
         *
         * @function instantiateModalTemplate
         * @memberof accuaCookieConsent
         */
        instantiateModalTemplate: function () {
            const templates = qsa('template.accua-modal-template[data-accua-lang]');
            if (!templates.length) {
                return; // Modal markup rendered directly (no templates)
            }

            const lang = this.lang || (this.data && this.data.lang) || '';

            let chosen = null;
            let fallback = null;
            templates.forEach(function (tpl) {
                if (tpl.getAttribute('data-accua-lang') === lang) {
                    chosen = tpl;
                }
                if (tpl.hasAttribute('data-accua-default')) {
                    fallback = tpl;
                }
            });
            chosen = chosen || fallback || templates[0];

            d.body.appendChild(chosen.content.cloneNode(true));

            templates.forEach(remove);
        },

        /**
         * Strip the one-time "accua_cc" query parameter added by the
         * revocation reload, so it never lingers in the address bar.
         *
         * @function stripReloadMarker
         * @memberof accuaCookieConsent
         */
        stripReloadMarker: function () {
            if (!window.history || !window.history.replaceState) return;
            if (!/[?&]accua_cc=/.test(window.location.search)) return;

            const clean = window.location.search.replace(/([?&])accua_cc=[^&]*(&?)/, function (m, sep, amp) {
                return amp ? sep : '';
            });
            window.history.replaceState(null, '', window.location.pathname + clean + window.location.hash);
        },

        /**
         * Reset frames of blocked elements that the browser restored from
         * session history (Firefox does this on F5/reload), so their content
         * matches the blocked src attribute again.
         *
         * @function neutralizeRestoredFrames
         * @memberof accuaCookieConsent
         */
        neutralizeRestoredFrames: function () {
            qsa('iframe[data-blocked]').forEach(function (frame) {
                try {
                    // Navigating a cross-origin frame is allowed; harmless when
                    // the frame is already blank
                    frame.contentWindow.location.replace('about:blank');
                } catch (e) {
                    // Fallback: replace the node to get a fresh browsing context
                    if (frame.parentNode) {
                        frame.parentNode.replaceChild(frame.cloneNode(true), frame);
                    }
                }
            });
        },

        /**
         * Setup all event listeners
         *
         * @function setupEventListeners
         * @memberof accuaCookieConsent
         */
        setupEventListeners: function () {
            const self = this;

            // Accept button click
            on('#accua_cookie_btn, .accua-cookie', 'click', function (e) {
                e.preventDefault();

                // Use setTimeout to ensure DOM is ready and checkboxes are available
                setTimeout(function () {
                    // Check all checkboxes
                    qsa(".accua_cookie_wrapper input[type='checkbox']").forEach(function (cb) {
                        cb.checked = true;
                    });

                    // Process the consent
                    self.processConsent(true, "");
                }, 50);
                qsa('#overlay-plugin-accua').forEach(hide);
            });

            // Update button click
            on('.accua_cookie_btn_update, .accua-cookie', 'click', function () {
                qsa('.update-message-box').forEach(show);
                self.processConsent(true, this.closest('.accua_cookie_box') || undefined);
                setTimeout(function () {
                    qsa('.update-message-box').forEach(hide);
                }, 6000);
            });

            // Decline button click
            on('#accua_cookie_btn_decline', 'click', function () {
                self.processConsent(true, "");
                qsa('#overlay-plugin-accua').forEach(hide);
            });

            // Close (X) button click
            on('#cookie_consent_close_x', 'click', function () {
                const decline = d.getElementById('accua_cookie_btn_decline');
                if (decline) {
                    decline.click();
                }
            });

            // Show cookie dialog via shortcode (use event delegation for dynamically added elements)
            delegate('click', '.showdialog-shortcode', function (e) {
                e.preventDefault();
                self.openDialog();
            });

            // Floating cookie icon click handler
            delegate('click', '.accua-cookie-floating-icon', function (e) {
                e.preventDefault();
                self.openDialog();
            });

            // Handle "More Info" link clicks
            on('.fom', 'click', function (event) {
                if (this.getAttribute('href') === '#') {
                    qsa('.accua_cookie_more_info_popover_generic').forEach(function (el) {
                        fadeIn(el, 'slow');
                    });
                    qsa('#obfuscator').forEach(function (el) {
                        fadeIn(el, 'fast');
                    });
                    window.scrollTo(0, 0);
                    event.preventDefault();
                }
            });

            // Close generic info popover
            on('#accua_close, #accua_close_x', 'click', function () {
                self.closeMoreInfoGeneric();
            });
        },

        /**
         * Open the consent dialog (floating icon, [accua-cookie-dialog] links).
         *
         * @function openDialog
         * @memberof accuaCookieConsent
         */
        openDialog: function () {
            // Check if using the accessible modal
            if (typeof window.AccuaAccessibleModal !== 'undefined' && window.AccuaAccessibleModal.open) {
                // Clear any inline display style first (may have been set by close)
                qsa('.accua_cookie_wrapper').forEach(function (el) {
                    el.style.display = '';
                });
                window.AccuaAccessibleModal.open();
            } else {
                qsa('.accua_cookie_wrapper').forEach(function (el) {
                    fadeIn(el, 'slow');
                });
            }
        },

        /**
         * Initialize UI elements based on current cookie state
         *
         * @function initializeUI
         * @memberof accuaCookieConsent
         */
        initializeUI: function () {
            const self = this;

            if (this.isBot) {
                // "Skip bots": no dialog, no placeholders, consent cookie
                // ignored; only content embeds load (see unblockForCrawler()).
                this.unblockForCrawler();
                return;
            }

            // Apply position classes
            this.applyPositionClasses();

            // Initialize floating cookie icon
            this.initFloatingIcon();

            // Check if cookie exists and is not empty
            if (this.currentCookieValue !== null && this.currentCookieValue !== "") {
                this.updateButtonText(this.data.buttonUpdateText);
                this.loadExistingPreferences();
            } else {
                // First visit or cookie deleted
                this.showBlockedContent();
                this.updateButtonText(this.data.buttonAcceptText);

                // Skip showing wrapper for centered - handled by scripts-modal.js
                if (this.data.position !== 'centered' && !this.popupDisabled) {
                    qsa('.accua_cookie_wrapper').forEach(show);
                    qsa('#overlay-plugin-gdpr').forEach(show);
                }
            }

            // Setup description handlers for all cookie classes
            each(this.data.classesData, function (k, v) {
                self.setCloseDescriptionHandler(v.name);
                self.setOpenDescriptionHandler(v.name);
            });
        },

        /**
         * Load blocked content embeds for a crawler.
         *
         * With "Salta il banner per i motori di ricerca" on, crawlers get no
         * dialog, and the videos and maps in the page (iframe, embed, object)
         * load as if consented, so they are seen and indexed. Scripts and
         * images stay blocked: they are where tracking lives (an image can be
         * a tracking pixel), and nothing should record a crawler's visit.
         *
         * @function unblockForCrawler
         * @memberof accuaCookieConsent
         */
        unblockForCrawler: function () {
            qsa('body iframe[data-blocked], body embed[data-blocked], body object[data-blocked]').forEach(function (el) {
                const attrName = el.nodeName === 'OBJECT' ? 'data' : 'src';
                el.setAttribute(attrName, el.getAttribute('data-blocked'));
                el.removeAttribute('data-blocked');
            });
        },

        /**
         * Initialize the floating cookie icon
         * Handles visibility based on consent state and responsive positioning
         *
         * @function initFloatingIcon
         * @memberof accuaCookieConsent
         */
        initFloatingIcon: function () {
            const self = this;
            const icons = qsa('.accua-cookie-floating-icon');

            if (!icons.length) {
                return; // Icon not present on page
            }

            // Get visibility setting from data attribute
            const visibility = icons[0].getAttribute('data-visibility') || 'always';
            const cookieExists = this.currentCookieValue !== null && this.currentCookieValue !== "";

            // Determine if icon should be shown based on visibility setting
            if (visibility === 'after_consent' && !cookieExists) {
                icons.forEach(function (icon) {
                    icon.classList.add('accua-cookie-floating-icon--hidden');
                });
            }

            // Setup responsive positioning
            this.updateFloatingIconPosition();
            window.addEventListener('resize', function () {
                self.updateFloatingIconPosition();
            });
        },

        /**
         * Update floating icon position based on current viewport width
         * Reads responsive settings from data attributes
         *
         * @function updateFloatingIconPosition
         * @memberof accuaCookieConsent
         */
        updateFloatingIconPosition: function () {
            const icons = qsa('.accua-cookie-floating-icon');

            if (!icons.length) {
                return;
            }
            const icon = icons[0];

            const windowWidth = d.documentElement.clientWidth;
            const breakpointTablet = parseInt(icon.getAttribute('data-breakpoint-tablet') || 1024, 10);
            const breakpointMobile = parseInt(icon.getAttribute('data-breakpoint-mobile') || 480, 10);

            // Determine current device type
            let device = 'desktop';
            if (windowWidth <= breakpointMobile) {
                device = 'mobile';
            } else if (windowWidth <= breakpointTablet) {
                device = 'tablet';
            }

            // Check if icon is enabled for this device. Read as a string: until
            // 6.8.0 jQuery's .data() turned "0" into the number 0, this check
            // never matched, and disabling the icon per device had no effect.
            const isEnabled = icon.getAttribute('data-' + device + '-enabled') !== '0';
            const side = icon.getAttribute('data-' + device + '-side') || 'right';

            icons.forEach(function (el) {
                // Toggle hidden state based on device enablement
                el.classList.toggle('accua-cookie-floating-icon--device-hidden', !isEnabled);

                // Update position class
                el.classList.remove('accua-cookie-floating-icon--left', 'accua-cookie-floating-icon--right');
                el.classList.add('accua-cookie-floating-icon--' + side);
            });
        },

        /**
         * Show the floating icon (called after consent is given)
         *
         * @function showFloatingIcon
         * @memberof accuaCookieConsent
         */
        showFloatingIcon: function () {
            qsa('.accua-cookie-floating-icon').forEach(function (icon) {
                if (icon.getAttribute('data-visibility') === 'after_consent') {
                    icon.classList.remove('accua-cookie-floating-icon--hidden');
                }
            });
        },

        /**
         * Hide the floating icon when modal is open
         *
         * @function hideFloatingIcon
         * @memberof accuaCookieConsent
         */
        hideFloatingIcon: function () {
            qsa('.accua-cookie-floating-icon').forEach(function (icon) {
                icon.classList.add('accua-cookie-floating-icon--hidden');
            });
        },

        /**
         * Update the text on consent buttons
         *
         * @function updateButtonText
         * @memberof accuaCookieConsent
         * @param {string} text - Text to display on buttons
         */
        updateButtonText: function (text) {
            if (text == null) {
                return; // jQuery's .text(undefined) was a getter: leave the buttons alone
            }
            qsa('.accua_cookie_btn_update').forEach(function (btn) {
                btn.textContent = text;
            });
        },

        /**
         * Apply position classes to elements
         *
         * @function applyPositionClasses
         * @memberof accuaCookieConsent
         */
        applyPositionClasses: function () {
            const self = this;

            each(this.data.positionClasses, function (k, v) {
                if (v && v.active) {
                    // The span must carry a configured category: showBlockedContent()
                    // and activateBlockedElements() look it up by that name.
                    const category = self.resolveCategory(v.class);
                    qsa(v.position).forEach(function (el) {
                        const message = self.blockedMessageFor(category);
                        if (message) {
                            el.insertAdjacentHTML('afterend',
                                '<span class="blockedContent-' + category + '">' + message + '</span>'
                            );
                        }
                    });
                }
            });
        },

        /**
         * The configured category a rule's class stands for.
         *
         * A legacy slug ("targeting") stands for its successor ("marketing") when
         * the configuration has the successor and not the legacy slug itself; any
         * other class is returned as is.
         *
         * @function resolveCategory
         * @memberof accuaCookieConsent
         * @param {string} categoryClass - Category slug from a rule
         * @returns {string} Category slug
         */
        resolveCategory: function (categoryClass) {
            const classes = (this.data && this.data.classesData) || {};
            if (typeof categoryClass === 'string' && hasOwn(LEGACY_CATEGORIES, categoryClass)
                && !hasOwn(classes, categoryClass) && hasOwn(classes, LEGACY_CATEGORIES[categoryClass])) {
                return LEGACY_CATEGORIES[categoryClass];
            }
            return categoryClass;
        },

        /**
         * The "content blocked" message for a category, in the resolved UI language.
         *
         * @function blockedMessageFor
         * @memberof accuaCookieConsent
         * @param {string} categoryClass - Cookie category slug
         * @returns {string} Message HTML, or '' when the category is unknown
         */
        blockedMessageFor: function (categoryClass) {
            const data = this.data;
            if (!data || !data.blockedMessage || !data.classesData) {
                return '';
            }

            const category = data.classesData[this.resolveCategory(categoryClass)];
            if (category === undefined) {
                return '';
            }

            // split/join, not replace(): replace() with a string pattern only swaps
            // the first occurrence, and {category} may appear more than once.
            return data.blockedMessage.split('{category}').join(category.label);
        },

        /**
         * Fill the placeholders PHP renders in place of blocked embeds.
         *
         * They ship empty: that markup goes into the page cache and is served to
         * every visitor, so the server cannot know which language to write. Called
         * from init() once applyLanguage() has resolved the browser language, and
         * before showBlockedContent() can reveal any of them.
         *
         * @function fillBlockedPlaceholders
         * @memberof accuaCookieConsent
         */
        fillBlockedPlaceholders: function () {
            const self = this;

            qsa('[data-accua-blocked-category]').forEach(function (placeholder) {
                const message = self.blockedMessageFor(placeholder.getAttribute('data-accua-blocked-category'));
                if (message) {
                    placeholder.innerHTML = message;
                }
            });
        },

        /**
         * Load existing preferences from cookie
         *
         * @function loadExistingPreferences
         * @memberof accuaCookieConsent
         */
        loadExistingPreferences: function () {
            const self = this;

            // Split cookie value by pipe character
            const cookieSettings = this.currentCookieValue.split('|');

            // Process each setting — use category name for lookups/selectors
            cookieSettings.forEach(function (v) {
                const setting = v.split(':');
                const catKey = setting[0];
                // Look up by key; also try finding by name field for robustness
                var catData = hasOwn(self.data.classesData || {}, catKey) ? self.data.classesData[catKey] : undefined;
                if (typeof catData === 'undefined') {
                    // Fallback: search classesData entries by name field
                    each(self.data.classesData, function (dk, dv) {
                        if (dv.name === catKey) {
                            catData = dv;
                            return false; // break
                        }
                    });
                }
                if (typeof catData !== 'undefined') {
                    var catName = catData.name;
                    var granted = setting[1] === 'set';
                    self.preferences[catName] = granted;
                    (granted ? self.accepted : self.denied).push(catName);
                    qsa('.checkbox-' + catName).forEach(function (cb) {
                        cb.checked = granted;
                    });
                }
            });

            // Check for new cookie classes since user's last consent
            let newSettings = false;
            each(this.data.classesData, function (k, v) {
                if (typeof self.preferences[v.name] === 'undefined') {
                    newSettings = true;
                }
            });
            this.newSettingsPending = newSettings;

            if (newSettings && this.popupDisabled) {
                // New categories appeared, but this page never prompts. Honour
                // what the visitor already accepted and leave the new
                // categories blocked (with their placeholders) until they open
                // the dialog themselves; re-prompting has to wait for a page
                // that is allowed to ask.
                this.showBlockedContent();
                this.activateBlockedElements(this.denied, this.accepted);
            } else if (newSettings) {
                // Show consent dialog for new settings
                this.showBlockedContent();

                // Skip showing wrapper for centered - handled by scripts-modal.js
                if (this.data.position !== 'centered') {
                    qsa('.accua_cookie_wrapper').forEach(show);
                    qsa('#overlay-plugin-accua').forEach(show);
                } else if (typeof window.AccuaAccessibleModal !== 'undefined') {
                    // For centered, trigger the accessible modal
                    window.AccuaAccessibleModal.open();
                }
            } else {
                // Apply existing preferences
                this.activateBlockedElements(this.denied, this.accepted);
            }
        },

        /**
         * Get whether a cookie class is checked by default
         *
         * @function getDefaultChecked
         * @memberof accuaCookieConsent
         * @param {string} name - Cookie class name
         * @returns {boolean} True if the class is enabled by default
         */
        getDefaultChecked: function (name) {
            let checked = false;

            each(this.data.classesData, function (k, v) {
                if (v.name === name) {
                    checked = v.default == 1;
                    return false; // break
                }
            });

            return checked;
        },

        /**
         * Show all blocked content
         *
         * @function showBlockedContent
         * @memberof accuaCookieConsent
         */
        showBlockedContent: function () {
            each(this.data.classesData, function (k, v) {
                qsa('.blockedContent-' + v.name).forEach(show);
            });
        },

        /**
         * Close the "More Info" generic popup
         *
         * @function closeMoreInfoGeneric
         * @memberof accuaCookieConsent
         */
        closeMoreInfoGeneric: function () {
            qsa('#obfuscator').forEach(function (el) {
                fadeOut(el, 'fast');
            });
            qsa('.accua_cookie_more_info_popover_generic').forEach(function (el) {
                fadeOut(el, 'slow');
            });
        },

        /**
         * Set handler for closing cookie class description
         *
         * @function setCloseDescriptionHandler
         * @memberof accuaCookieConsent
         * @param {string} name - Cookie class name
         */
        setCloseDescriptionHandler: function (name) {
            on('#accua_close_' + name + ', #accua_close_x_' + name, 'click', function () {
                qsa('#obfuscator').forEach(function (el) {
                    fadeOut(el, 'fast');
                });
                qsa('.description_popover_' + name).forEach(function (el) {
                    fadeOut(el, 'slow');
                });
            });
        },

        /**
         * Set handler for opening cookie class description
         *
         * @function setOpenDescriptionHandler
         * @memberof accuaCookieConsent
         * @param {string} name - Cookie class name
         */
        setOpenDescriptionHandler: function (name) {
            on('.accua_open_' + name, 'click', function () {
                qsa('.description_popover_' + name).forEach(function (el) {
                    fadeIn(el, 'slow');
                });
                qsa('#obfuscator').forEach(function (el) {
                    fadeIn(el, 'fast');
                });
                window.scrollTo(0, 0);
            });
        },

        /**
         * Get all cookie names currently set in the browser
         *
         * @function getAllBrowserCookieNames
         * @memberof accuaCookieConsent
         * @returns {Array} Array of cookie name strings
         */
        getAllBrowserCookieNames: function () {
            if (!document.cookie) return [];
            return document.cookie.split(';').map(function (c) {
                return c.trim().split('=')[0];
            });
        },

        /**
         * Delete cookies matching a pattern (supports * wildcards)
         * Converts pattern like "_ga_*" to a regex and matches against
         * all cookies currently in the browser.
         *
         * @function deleteCookiesByPattern
         * @memberof accuaCookieConsent
         * @param {string} pattern - Cookie name or wildcard pattern
         */
        deleteCookiesByPattern: function (pattern) {
            const self = this;
            if (!pattern) return;

            // Escape regex special chars, then convert * wildcard to .*
            var regexStr = '^' + pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*') + '$';
            var regex = new RegExp(regexStr);

            var allCookies = this.getAllBrowserCookieNames();
            var apexDomain = /\.[^.]+\.[^.]+$/i.exec(window.location.hostname);

            allCookies.forEach(function (name) {
                if (regex.test(name)) {
                    // Delete with multiple path/domain combinations
                    self.setCookie(name, '', -1, '');
                    if (apexDomain) {
                        self.setCookie(name, '', -1, apexDomain[0]);
                    }
                }
            });
        },

        /**
         * Process user consent (accept or decline)
         *
         * @function processConsent
         * @memberof accuaCookieConsent
         * @param {boolean} isApproved - Whether consent was approved
         * @param {HTMLElement|string} parent - Parent element or empty string
         */
        processConsent: function (isApproved, parent) {
            const self = this;

            // Save old preferences to detect revocations
            const oldPreferences = extend({}, this.preferences);
            const hadPreviousConsent = this.currentCookieValue !== null && this.currentCookieValue !== "";

            this.updateButtonText(this.data.buttonUpdateText);
            this.preferences = {};
            this.denied = [];
            this.accepted = [];

            // No wrapper (or no box) means the whole document, as jQuery did
            const parentElement = (parent === "" ? d.querySelector('.accua_cookie_wrapper') : parent) || d;
            let hasRevocation = false;

            // Process each cookie class
            each(this.data.classesData, function (k, v) {
                const checkbox = parentElement.querySelector('.checkbox-' + v.name);
                if (checkbox != null) {
                    const isDenied = !checkbox.checked || (!isApproved && v.name !== "");
                    self.preferences[v.name] = !isDenied;

                    if (isDenied) {
                        self.denied.push(v.name);
                        // Delete cookies for denied categories (wildcard-aware)
                        if (v.cookies) {
                            var patterns = v.cookies.replace(/\s/g, '').split(';');
                            patterns.forEach(function (pat) {
                                if (pat) self.deleteCookiesByPattern(pat);
                            });
                        }
                        // Track if this was previously accepted (revocation)
                        if (hadPreviousConsent && oldPreferences[v.name] === true) {
                            hasRevocation = true;
                        }
                    } else {
                        self.accepted.push(v.name);
                    }
                }
            });

            this.createConsentCookie();
            this.currentCookieValue = this.getCookie();
            this.newSettingsPending = false;

            // If a previously-accepted category was revoked, reload the page
            // so PHP re-blocks the scripts from server side.
            // Navigate to a unique URL instead of location.reload(): a plain
            // reload lets Firefox restore each iframe's session history, so a
            // just-revoked embed would still display (and load) its old
            // content. A distinct URL forces fresh subframe contexts and
            // bypasses any cached copy of the page; the marker parameter is
            // stripped again in init() via history.replaceState.
            if (hasRevocation) {
                let url = window.location.href.split('#')[0];
                url += (url.indexOf('?') === -1 ? '?' : '&') + 'accua_cc=' + Date.now();
                window.location.replace(url);
                return;
            }

            this.activateBlockedElements(this.denied, this.accepted);
        },

        /**
         * Create the consent cookie with user preferences
         *
         * @function createConsentCookie
         * @memberof accuaCookieConsent
         */
        createConsentCookie: function () {
            const value = [];

            each(this.preferences, function (k, v) {
                value.push(k + ":" + (v ? 'set' : 'no'));
            });

            const cookieValue = encodeURIComponent(value.join("|"));
            const expireDays = this.data.expireTimer > 0 ? this.data.expireTimer : 0;

            this.setCookie("accuaCookieConsent", cookieValue, expireDays, "");

            // centered modal is closed by scripts-modal.js, skip fadeOut
            if (this.data.position !== 'centered') {
                qsa('.accua_cookie_wrapper').forEach(function (el) {
                    fadeOut(el, 'fast');
                });
                qsa('#overlay-plugin-accua').forEach(hide);
            }
        },

        /**
         * Set a cookie with the specified parameters
         *
         * @function setCookie
         * @memberof accuaCookieConsent
         * @param {string} name - Cookie name
         * @param {string} value - Cookie value
         * @param {number} days - Days until expiration
         * @param {string} domain - Cookie domain
         */
        setCookie: function (name, value, days, domain) {
            let expires = "";

            if (days) {
                const date = new Date();
                date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
                expires = "; expires=" + date.toUTCString();
            }

            domain = domain ? ";domain=" + domain : "";
            document.cookie = name + "=" + value + expires + domain + ";path=/;SameSite=Lax";
        },

        /**
         * Get the Accua cookie consent value
         *
         * @function getCookie
         * @memberof accuaCookieConsent
         * @returns {string|null} Cookie value or null if not found
         */
        getCookie: function () {
            const allCookies = document.cookie;

            if (allCookies === "") {
                return null;
            }

            const cookieList = allCookies.split("; ");
            for (let i = 0; i < cookieList.length; i++) {
                const cookie = cookieList[i];
                const separatorIndex = cookie.indexOf("=");
                const name = cookie.substring(0, separatorIndex);

                if (name === 'accuaCookieConsent') {
                    return decodeURIComponent(cookie.substring(separatorIndex + 1));
                }
            }

            return null;
        },

        /**
         * Delete the Accua cookie consent
         *
         * @function deleteCookie
         * @memberof accuaCookieConsent
         */
        deleteCookie: function () {
            this.setCookie('accuaCookieConsent', '', -1, "");
        },

        /**
         * Reload JavaScript from a blocked script
         *
         * @function reloadJs
         * @memberof accuaCookieConsent
         * @param {string} src - Script source
         */
        reloadJs: function (src) {
            qsa('script[data-blocked]').forEach(function (el) {
                if (el.getAttribute('data-blocked') === src) {
                    remove(el);
                }
            });
            const script = d.createElement('script');
            script.src = src;
            // Dynamically inserted scripts run in any order unless told
            // otherwise; keep the page order of the scripts being released.
            script.async = false;
            d.body.appendChild(script);
        },

        /**
         * Activate or deactivate blocked elements based on user consent
         *
         * @function activateBlockedElements
         * @memberof accuaCookieConsent
         * @param {Array} denied - Array of denied cookie classes
         * @param {Array} accepted - Array of accepted cookie classes
         */
        activateBlockedElements: function (denied, accepted) {
            const self = this;

            // Show blocked content for denied classes
            denied.forEach(function (v) {
                qsa('.blockedContent-' + v).forEach(show);
            });

            // Remove blocked content for accepted classes
            accepted.forEach(function (v) {
                qsa('.blockedContent-' + v).forEach(remove);
            });

            // Elements of a page cached before 6.8.1 can still carry a renamed
            // category class ("targeting"): accepting its successor releases them.
            const acceptedClasses = accepted.slice();
            each(LEGACY_CATEGORIES, function (legacy, current) {
                if (accepted.indexOf(current) !== -1 && self.resolveCategory(legacy) === current) {
                    acceptedClasses.push(legacy);
                    qsa('.blockedContent-' + legacy).forEach(remove);
                }
            });

            // A blocked element can carry a category class that is not in the
            // configuration at all -- a JSON import brings script rules over but
            // only updated categories that already existed, so a rule could
            // outlive its category. Such a class is in neither `denied` nor
            // `accepted`, and excluding only the denied ones would unblock it
            // for every visitor, including one who refused everything. Filter
            // positively on the accepted categories instead, exactly as the
            // text/blocked branch below already does. With nothing denied every
            // category is consented, so unblocking everything stays correct.
            const isConsented = function (el) {
                if (!denied.length) {
                    return true;
                }
                return accepted.length > 0 && hasClassName(el, acceptedClasses);
            };

            // Reload blocked scripts that are now accepted
            qsa('script[data-blocked]').filter(isConsented).forEach(function (el) {
                self.reloadJs(el.getAttribute('data-blocked'));
            });

            // Unlock iframes, images, embeds and other elements blocked with 'data-blocked'
            qsa('body [data-blocked]').filter(function (el) {
                return el.nodeName !== 'SCRIPT' && isConsented(el);
            }).forEach(function (el) {
                var blockedUrl = el.getAttribute('data-blocked');
                // <object> tags use 'data' attribute instead of 'src'
                var attrName = el.nodeName === 'OBJECT' ? 'data' : 'src';
                el.setAttribute(attrName, blockedUrl);
                el.removeAttribute('data-blocked');
            });

            // Unlock embed scripts with 'type = text/blocked'
            // Use positive filter: only unblock scripts that have an explicit
            // accepted-category CSS class. This prevents unblocking classless
            // scripts (e.g. theme-hardcoded type="text/blocked" without a
            // category class) when some categories are denied.
            qsa("script[type='text/blocked']").filter(function (el) {
                // All categories accepted: safe to unblock everything;
                // otherwise only scripts with an accepted category class
                return !denied.length || (accepted.length > 0 && hasClassName(el, acceptedClasses));
            }).forEach(function (el) {
                el.setAttribute('type', 'text/javascript');
                globalEval(el.text);
            });
        }
    };

    // Expose to global scope for use by modal
    window.AccuaCookieConsent = accuaCookieConsent;

    // Initialize on document ready, once js/boot.js has the visitor's language
    onReady(function () {
        const boot = typeof window.accuaCookieBootRun === 'function' ? window.accuaCookieBootRun() : null;
        if (boot && boot.whenReady) {
            boot.whenReady(function () {
                accuaCookieConsent.init();
            });
        } else {
            accuaCookieConsent.init();
        }
    });

})();
